import React from 'react';
import { get, every, map, sumBy, isFinite, isUndefined, isEmpty } from 'lodash';
import { FormattedMessage } from 'react-intl';

import { GENDER_NON_BINARY_VALUE } from 'containers/TargetingPeeqPage/constants';

import {
  getCurrentGroupValues,
  getUngroupedValues,
} from 'components/GroupAnswersModal/utils';
import generalMessages from './messages';

const MAX_ALLOWED_VALUES_WITH_QUOTA = 100;

export const canHaveQuotas = (selections = []) =>
  selections.length > 1 && selections.length <= MAX_ALLOWED_VALUES_WITH_QUOTA;

export const clearQuotaCompletesFromValues = (values) =>
  values.map((value) => ({
    ...value,
    quotaCompletes: undefined,
  }));

export const getTransformedValues = ({ values, completes, isQuotasEnabled }) =>
  isQuotasEnabled
    ? values.map((value) => {
        if (isUndefined(value.quotaCompletes)) {
          return {
            ...value,
            quotaCompletes: completes,
          };
        }

        return value;
      })
    : clearQuotaCompletesFromValues(values);

export const getInitializedQuota = (values, enabled, completes) => {
  if (!enabled) {
    return values;
  }

  const quotaIsNotInitialized = every(
    values,
    (value) => get(value, 'quotaCompletes', false) === false,
  );

  if (quotaIsNotInitialized) {
    const count = values.length;
    const itemQuotaCompletes = Math.floor(completes / count);
    const lastItemQuotaCompletes = completes - itemQuotaCompletes * (count - 1);
    const lastItemRefId = values[count - 1].refId;

    return values.map((value) => {
      const isLastItem = value.refId === lastItemRefId;
      const quotaCompletes = isLastItem
        ? lastItemQuotaCompletes
        : itemQuotaCompletes;

      return { ...value, quotaCompletes };
    });
  }

  return values;
};

export function getPeeqCriteriaQuotaError({
  values,
  completes,
  surveyConfig = {},
}) {
  const isManagedTargeting = surveyConfig.managedTargeting;
  const allValuesWithQuota = map(values, 'quotaCompletes').filter(isFinite);
  // if not all values have associated quota then there is not error
  // since the "open" values will allow the audience to be filled
  if (allValuesWithQuota.length !== values.length) return null;

  const quotaSum = sumBy(values, 'quotaCompletes');
  if (quotaSum < completes && !isManagedTargeting) {
    return (
      <FormattedMessage
        {...generalMessages.quotaSumError}
        values={{ completes }}
      />
    );
  }

  return null;
}

export const getGroupAnswersData = (group, data) => {
  // during edit of a group
  if (group) {
    // criterionOptions is not available on postal code criterion
    const criterionOptions = !isEmpty(data.criterionOptions)
      ? data.criterionOptions
      : getCurrentGroupValues(data.values, group.refId).map((value) => ({
          ...value,
          text: value.value,
        }));

    return {
      ...data,
      refId: group.refId,
      groupName: group.groupName,
      criterionOptions,
      // don't show card's batch text inside pop up during the edit of a group
      batch: undefined,
    };
  }

  // during creation of a new group
  const criterionOptions = !isEmpty(data.criterionOptions)
    ? data.criterionOptions
    : getUngroupedValues(data.values).map((value) => ({
        ...value,
        text: value.value,
      }));

  return {
    ...data,
    criterionOptions,
    refId: undefined,
    groupName: '',
  };
};

const hasNonBinaryGenderOnList = (list) =>
  list.some((item) => {
    if (item.isGroup) {
      return hasNonBinaryGenderOnList(item.values);
    }

    return item.value === GENDER_NON_BINARY_VALUE;
  });

export const hasAddedNonBinaryGender = ({
  isGenderCriterion,
  previousValues,
  newValues,
}) => {
  if (!isGenderCriterion) {
    return false;
  }

  const hasNonBinaryGender = hasNonBinaryGenderOnList(newValues);
  const hadNonBinaryGender = hasNonBinaryGenderOnList(previousValues);

  return hasNonBinaryGender && !hadNonBinaryGender;
};

// at the moment is used for prepaid surveys where gender is required
export const ensureThatAtLeastOnGenderValueIsSelectedWhenRequired = ({
  isGenderCriterion,
  isRequired,
  previousValues,
  newValues,
}) => {
  if (isGenderCriterion && isRequired && newValues.length === 0) {
    return previousValues[0].isGroup
      ? [previousValues[0].values[0]]
      : [previousValues[0]];
  }

  return newValues;
};

export const flatAllValues = (values) =>
  values.reduce((accumulator, item) => {
    if (item.isGroup) {
      return [...accumulator, ...item.values];
    }

    return [...accumulator, item];
  }, []);

export function flatMapAllValues(values) {
  return values.reduce((accumulator, item) => {
    if (item.isGroup) {
      return [...accumulator, ...item.values.map(({ value }) => value)];
    }

    return [...accumulator, item.value];
  }, []);
}
